import React, { useEffect, useMemo, useState } from 'react';
import { getCustomerSessionAccountId } from './kioskCustomerSession';
import { stockChoiceValid } from './StockRecipeChoices';
import OrgasmeatEmoji, { getOrgasmeatEmoji, getOrgasmeatDisplayName } from './OrgasmeatEmoji';
import OrgasmeatActionIcon from './OrgasmeatActionIcon';
import './OrgasmeatExperience.css';

export const ORGASMEAT_TOKENS = {
  '--kiosk-bg': '#ffffeb', '--kiosk-surface': '#ffffeb', '--kiosk-panel': '#ffffeb',
  '--kiosk-primary': '#51182f', '--kiosk-primary-dark': '#381021', '--kiosk-accent': '#d0b0f4',
  '--kiosk-ink': '#51182f', '--kiosk-muted': '#765568',
};
export const isOrgasmeatExperience = settings => settings?.kioskExperience === 'orgasmeat-v1';
const formatPrice = value => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value));

function getOrgasmeatExtraKind(name) {
  const normalized = String(name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  if (['a cote', 'side', 'sides', 'accompagnement', 'accompagnements'].includes(normalized)) return 'side';
  if (['boisson', 'boissons', 'drink', 'drinks'].includes(normalized)) return 'drink';
  if (['happy ending', 'dessert', 'desserts'].includes(normalized)) return 'dessert';
  return null;
}

// Use only the current tenant's available catalogue, never an unrelated bestseller.
// Menu selections count too: a drink already included in a menu needs no reminder.
export function getOrgasmeatUpsells(products, cart, sections = []) {
  const category = product => getOrgasmeatExtraKind(product.Section?.name
    || sections.find(section => String(section.id) === String(product.sectionId))?.name);
  const present = new Set();
  const ids = new Set();
  const include = (product, sectionName) => {
    const id = product.productId ?? product.id;
    const catalogueProduct = products.find(candidate => sameMenuId(candidate.id, id));
    ids.add(String(id));
    present.add(category(product) || (catalogueProduct && category(catalogueProduct)) || getOrgasmeatExtraKind(sectionName));
  };
  cart.forEach(item => {
    if (item.isMenu) {
      (item.customSelections || []).forEach(selection => {
        (selection.products || []).forEach(product => include(product, selection.sectionName));
      });
    } else include(item);
  });
  return ['side', 'drink', 'dessert'].filter(kind => !present.has(kind)).map(kind => products.find(product =>
    category(product) === kind && !ids.has(String(product.id))
    && product.isBorneAvailable !== false && product.isAvailable !== false
    && Number.isFinite(Number(product.price)) && Number(product.price) > 0
  )).filter(Boolean);
}

const menuMoneyCents = value => value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0
  && Number.isSafeInteger(Math.round(Number(value) * 100)) ? Math.round(Number(value) * 100) : null;
const menuExtraCents = product => menuMoneyCents(product.MenuProducts?.additionalPrice === undefined ? 0 : product.MenuProducts.additionalPrice);
const menuRequiredQuantity = section => Number(section.MenuSections?.requiredQuantity ?? section.requiredQuantity ?? 1);
const sameMenuId = (left, right) => String(left) === String(right);

// A drink upgrade is a real menu variant, never a discounted standalone product.
// Matching all existing choices and surcharges keeps this independent of names and demo flags.
export function getOrgasmeatMenuDrinkUpgrade(product, cart, menus = [], sections = []) {
  const kind = getOrgasmeatExtraKind(product.Section?.name || sections.find(section => sameMenuId(section.id, product.sectionId))?.name);
  if (kind !== 'drink' || !getOrgasmeatUpsells([product], cart, sections).length) return null;
  const available = item => item.isAvailable !== false && item.isBorneAvailable !== false;
  for (const source of cart) {
    if (!source.isMenu || !source.cartKey || !Number.isInteger(source.quantity) || source.quantity < 1) continue;
    const base = menus.find(menu => sameMenuId(menu.id, source.id));
    if (!base || !available(base) || menuMoneyCents(base.price) === null || menuMoneyCents(source.price) !== menuMoneyCents(base.price)) continue;
    const baseSections = base.Sections || [];
    if (!baseSections.length || new Set(baseSections.map(section => String(section.id))).size !== baseSections.length
      || baseSections.some(section => getOrgasmeatExtraKind(section.name) === 'drink' || !Number.isInteger(menuRequiredQuantity(section)) || menuRequiredQuantity(section) < 1)) continue;
    const selectedSections = source.customSelections || [];
    if (selectedSections.length !== baseSections.length) continue;
    const matches = menus.flatMap(menu => {
      const price = menuMoneyCents(menu.price);
      if (sameMenuId(menu.id, base.id) || !available(menu) || price === null || price < menuMoneyCents(base.price)) return [];
      const variantSections = menu.Sections || [];
      if (variantSections.length !== baseSections.length + 1 || new Set(variantSections.map(section => String(section.id))).size !== variantSections.length) return [];
      const extraSections = variantSections.filter(section => !baseSections.some(baseSection => sameMenuId(baseSection.id, section.id)));
      if (extraSections.length !== 1 || getOrgasmeatExtraKind(extraSections[0].name) !== 'drink' || menuRequiredQuantity(extraSections[0]) !== 1) return [];
      const drinkSection = extraSections[0];
      const drink = (menu.Products || []).find(item => sameMenuId(item.id, product.id) && sameMenuId(item.sectionId, drinkSection.id));
      if (!drink || !available(drink) || menuExtraCents(drink) === null) return [];
      const selections = {};
      let sourceTotal = menuMoneyCents(base.price);
      for (const section of baseSections) {
        const targetSection = variantSections.find(candidate => sameMenuId(candidate.id, section.id));
        if (!targetSection || menuRequiredQuantity(targetSection) !== menuRequiredQuantity(section)) return [];
        const baseChoices = (base.Products || []).filter(item => sameMenuId(item.sectionId, section.id));
        const targetChoices = (menu.Products || []).filter(item => sameMenuId(item.sectionId, section.id));
        if (!baseChoices.length || baseChoices.length !== targetChoices.length || new Set(baseChoices.map(item => String(item.id))).size !== baseChoices.length
          || baseChoices.some(item => {
            const target = targetChoices.find(candidate => sameMenuId(candidate.id, item.id));
            return !target || menuExtraCents(item) === null || menuExtraCents(item) !== menuExtraCents(target)
              || JSON.stringify(item.stockChoices || null) !== JSON.stringify(target.stockChoices || null);
          })) return [];
        const selected = selectedSections.find(candidate => sameMenuId(candidate.sectionId, section.id))?.products;
        if (!Array.isArray(selected) || selected.length !== menuRequiredQuantity(section)) return [];
        const preserved = selected.map(item => {
          const target = targetChoices.find(candidate => sameMenuId(candidate.id, item.id));
          if (!target || !available(target) || menuExtraCents(item) !== menuExtraCents(target)) return null;
          const next = { ...item, ...target, stockChoice: item.stockChoice };
          try { if (!stockChoiceValid(next)) return null; } catch (_) { return null; }
          return next;
        });
        if (preserved.some(item => !item)) return [];
        sourceTotal += preserved.reduce((total, item) => total + menuExtraCents(item), 0);
        selections[section.id] = preserved;
      }
      if (sourceTotal !== menuMoneyCents(source.totalPrice)) return [];
      return [{ sourceCartKey: source.cartKey, menu, drinkSectionId: drinkSection.id, drinkProduct: drink, selections,
        additionalPrice: (price - menuMoneyCents(base.price) + menuExtraCents(drink)) / 100 }];
    });
    if (matches.length === 1) return matches[0];
  }
  return null;
}

export function getOrgasmeatMenuOffers(product, menus = []) {
  const offers = menus.map(menu => {
    if (menu.isAvailable === false || menu.price === null || menu.price === '' || !Number.isFinite(Number(menu.price)) || Number(menu.price) < 0) return null;
    const sections = menu.Sections || [];
    const burger = (menu.Products || []).find(item => String(item.id) === String(product.id));
    if (!burger || !sections.some(section => String(section.id) === String(burger.sectionId)) || sections.length < 2 || sections.some(section => Number(section.MenuSections?.requiredQuantity ?? section.requiredQuantity ?? 1) !== 1)) return null;
    const otherKinds = sections.filter(section => String(section.id) !== String(burger.sectionId)).map(section => getOrgasmeatExtraKind(section.name));
    if (!otherKinds.includes('side') || otherKinds.some(kind => !kind)) return null;
    let extraCents = 0;
    for (const section of sections) {
      const choices = String(section.id) === String(burger.sectionId) ? [burger]
        : (menu.Products || []).filter(item => String(item.sectionId) === String(section.id));
      const prices = choices.filter(item => item.isAvailable !== false && item.isBorneAvailable !== false)
        .filter(item => item.MenuProducts?.additionalPrice !== null && item.MenuProducts?.additionalPrice !== '')
        .map(item => Number(item.MenuProducts?.additionalPrice ?? 0)).filter(value => Number.isFinite(value) && value >= 0);
      if (!prices.length) return null;
      extraCents += Math.min(...prices.map(value => Math.round(value * 100)));
    }
    return { menu, startingPrice: (Math.round(Number(menu.price) * 100) + extraCents) / 100 };
  }).filter(Boolean);
  return offers;
}

export function getOrgasmeatMenuOffer(product, menus = []) {
  const offers = getOrgasmeatMenuOffers(product, menus);
  return offers.length === 1 ? offers[0] : null;
}

// A complete, explicit map is required. Never infer a burger from its name, price or another tenant.
export function getOrgasmeatSteaks(settings, products) {
  if (!isOrgasmeatExperience(settings)) return [];
  let config = settings.kioskExperienceConfig;
  try { if (typeof config === 'string') config = JSON.parse(config); } catch (_) { return []; }
  const map = config?.steaks;
  if (!map || typeof map !== 'object' || Array.isArray(map) || Object.keys(map).length !== 6) return [];
  const ids = new Set();
  const options = Array.from({ length: 6 }, (_, index) => {
    const count = index + 1;
    const id = map[count];
    if (!/^[1-9]\d*$/.test(String(id)) || ids.has(String(id))) return null;
    ids.add(String(id));
    const product = (products || []).find(candidate => String(candidate.id) === String(id));
    if (!product || product.isBorneAvailable === false || product.isAvailable === false || !product.name || product.price === null || product.price === '' || !Number.isFinite(Number(product.price)) || Number(product.price) < 0) return null;
    return { count, product };
  });
  return options.every(Boolean) ? options : [];
}

function readOrgasmeatSettings() {
    const accountId = getCustomerSessionAccountId();
    try {
      const settings = accountId ? JSON.parse(localStorage.getItem(`maythai_kiosk_settings_cache_v1:${accountId}`) || 'null') : null;
      return { accountId, settings };
    } catch (_) { return { accountId, settings: null }; }
}

export function useOrgasmeatSettings() {
  const [snapshot, setSnapshot] = useState(readOrgasmeatSettings);
  useEffect(() => {
    const update = event => {
      const accountId = getCustomerSessionAccountId();
      if (event?.detail) {
        if (String(event.detail.accountId) === String(accountId) && accountId) setSnapshot({ accountId, settings: event.detail.settings });
        else setSnapshot(readOrgasmeatSettings());
      } else setSnapshot(readOrgasmeatSettings());
    };
    window.addEventListener('bekiosk:settings-updated', update);
    window.addEventListener('storage', update);
    window.addEventListener('focus', update);
    return () => {
      window.removeEventListener('bekiosk:settings-updated', update);
      window.removeEventListener('storage', update);
      window.removeEventListener('focus', update);
    };
  }, []);
  return snapshot.accountId === getCustomerSessionAccountId() ? snapshot.settings : null;
}

export function OrgasmeatBrand({ children }) {
  return <header className="orgasmeat-brand"><img src="/orgasmeat/logo.svg" alt="ORGASMEAT" /><span>{children || 'Le plaisir à tous les étages.'}</span></header>;
}

export function OrgasmeatBuilder({ settings, products, menus = [], onChoose, onChooseMenu }) {
  const options = useMemo(() => getOrgasmeatSteaks(settings, products), [settings, products]);
  const [count, setCount] = useState(1);
  if (!options.length) return null;
  const selected = options.find(option => option.count === count) || options[0];
  const { product } = selected;
  const menuOffers = getOrgasmeatMenuOffers(product, menus);
  const menuOffer = menuOffers.length ? { startingPrice: Math.min(...menuOffers.map(offer => offer.startingPrice)), menu: menuOffers.length === 1 ? menuOffers[0].menu : null } : null;
  return <section className="orgasmeat-builder" aria-labelledby="orgasmeat-builder-title">
    <div className="orgasmeat-builder-copy">
      <span className="orgasmeat-eyebrow">À toi de jouer</span>
      <h1 id="orgasmeat-builder-title">Du plaisir.<br /><em>À tous les étages.</em></h1>
      <p className="orgasmeat-builder-intro">De 1 à 6 steaks. Fais monter le plaisir.</p>
      <div className="orgasmeat-steaks" role="group" aria-label="Nombre de steaks">
        {options.map(option => <button key={option.count} type="button" aria-label={`${option.count} steak${option.count > 1 ? 's' : ''}`} aria-pressed={selected.count === option.count} onClick={() => setCount(option.count)}><strong>{option.count}</strong><span>{option.count === 1 ? 'steak' : 'steaks'}</span></button>)}
      </div>
      <div className="orgasmeat-selection" aria-live="polite" aria-atomic="true"><h2>{getOrgasmeatDisplayName(product.name)}</h2><strong>{formatPrice(product.price)}</strong></div>
      <div className="orgasmeat-order-actions">
        <button type="button" className="orgasmeat-action" onClick={() => onChoose(product)}>{menuOffer && onChooseMenu ? 'Burger seul' : 'Choisir ce burger'} <OrgasmeatActionIcon name="burger" /></button>
        {menuOffer && onChooseMenu && <button type="button" className="orgasmeat-action orgasmeat-action-menu" onClick={() => onChooseMenu(menuOffer.menu, product)}><span>En menu <small>dès {formatPrice(menuOffer.startingPrice)}</small></span><OrgasmeatActionIcon name="menu" /></button>}
      </div>
      <span className="orgasmeat-builder-note">{menuOffer && onChooseMenu ? 'Ton burger + un accompagnement. À toi de choisir ta formule.' : 'La recette et les options à l’étape suivante.'}</span>
    </div>
    <div className="orgasmeat-builder-visual" aria-hidden="true">
      <span className="orgasmeat-level">0{selected.count}</span>
      <img key={selected.count} src={`/orgasmeat/burger-${selected.count}.avif`} alt="" onError={event => { if (product.image && !event.currentTarget.dataset.fallback) { event.currentTarget.dataset.fallback = 'true'; event.currentTarget.src = product.image; } }} />
      <span key={`sticker-${selected.count}`} className={`orgasmeat-sticker${getOrgasmeatEmoji(product.name) ? ' has-emoji' : ''}`}><OrgasmeatEmoji name={product.name} className="orgasmeat-sticker-emoji" />{selected.count} steak{selected.count > 1 ? 's' : ''}<small>100 % plaisir</small></span>
    </div>
  </section>;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return undefined;
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

export function OrgasmeatAttract({ isOpen, message, reopeningLabel, onStart }) {
  const reducedMotion = useReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  return <section className="kiosk-orgasmeat orgasmeat-attract" style={ORGASMEAT_TOKENS}>
    <div className="orgasmeat-attract-stage">
      <img className="orgasmeat-attract-media" src="/orgasmeat/attract-user-poster.jpg" alt="" />
      {!reducedMotion && !videoFailed && <video className="orgasmeat-attract-media" src="/orgasmeat/attract-user.mp4" poster="/orgasmeat/attract-user-poster.jpg" autoPlay muted loop playsInline preload="metadata" aria-hidden="true" onError={() => setVideoFailed(true)} />}
      <OrgasmeatBrand />
      <button type="button" className="orgasmeat-attract-start" onClick={onStart} disabled={!isOpen} aria-label={isOpen ? 'Commencer ma commande ORGASMEAT' : message}>
        <span className="orgasmeat-attract-heading">Plus de steaks.<br /><em>Plus de plaisir.</em></span>
        <span className="orgasmeat-attract-bottom">
          <span className="orgasmeat-attract-caption">{isOpen ? 'Allez, on se fait plaisir ?' : message}</span>
          <span className="orgasmeat-attract-cta">
            <span className="orgasmeat-attract-cta-copy">
              <strong>{isOpen ? <>Touche pour<br />commander<span className="orgasmeat-attract-dot">.</span></> : reopeningLabel || 'À très bientôt'}</strong>
              {isOpen && <small>Ton prochain coup de food.</small>}
            </span>
            {isOpen && <span className="orgasmeat-attract-touch" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                <path d="M19 25V12a3 3 0 0 1 6 0v11l2-1a3 3 0 0 1 4 2l1-1a3 3 0 0 1 4 2 3 3 0 0 1 5 2v7c0 6-4 10-10 10h-4c-4 0-7-2-9-5l-6-10a3 3 0 0 1 4-4l3 3" />
                <path d="M22 2v2M10 8l2 2M32 8l2-2M7 18h3" />
              </svg>
            </span>}
          </span>
        </span>
      </button>
    </div>
    <div className="orgasmeat-ribbon" aria-hidden="true">SMASH. STACK. LOVE. <span>★</span> SMASH. STACK. LOVE. <span>★</span></div>
  </section>;
}
